'use client';

import { RoutePermissionGuard } from '@/@auth/components';
import { logRouteVisit, useDemoCookie, useExpiredLicense, useSessionError } from '@/@auth/hooks';
import { useMarkTabInUse } from '@/@auth/post-sign-in';
import { TrialNotificationBanner } from '@/@account/components/TrialNotificationBanner';
import { useDiscoverInsightsAutoRefresh } from '@/@discover/hooks/useDiscoverInsightsAutoRefresh';
import { PlannerReportToasts } from '@/@studio-ai/components/planner-mode/planner-report-toasts';

import { PersistentAssistant } from '@/@studio-ai/persistent-assistant';
import { FloatingInputBarProvider } from '@/@studio-ai/persistent-assistant/components/floating-input-bar-context';
import { OrgSelectionDrawer } from '@/@subsidiary/components/OrgSelectionDrawer';
import { ViewAsBanner } from '@/@subsidiary/components/ViewAsBanner';
import { SubsidiaryFilterProvider } from '@/@subsidiary/provider';

import { LayoutProps } from '@/@studio/types/props';
import SessionDebug from '@/components/shared/debug/SessionDebug';
import WithSidebar from '@/components/ui/templates/withSidebar/withSidebar';
import { ENV } from '@/lib/config/environment';
import { useSession } from '@/providers/enhanced-session';
import { Button, Image, useDisclosure } from '@heroui/react';
import { Icon } from '@iconify/react';
import { signOut } from 'next-auth/react';
import { LogRocketIdentify } from './LogRocketIdentify';
import NextImage from 'next/image';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';

const PrivateLayout = ({ children }: LayoutProps) => {
    const { data: session, status } = useSession();
    const pathname = usePathname();
    const { onOpenChange } = useDisclosure();

    const isFreemium = session?.user?.isUnaffiliated ?? false;
    const isAssistantEnabled =
        (session?.user?.hasStudioAIAccess || session?.org?.hasClientStudioAIAccess) ?? false;

    // Add session error hook to handle expired sessions
    useSessionError(session, status);

    // Add expired license hook to handle expired licenses
    useExpiredLicense(session, pathname);

    // Add demo cookie for internal users
    useDemoCookie(session);

    // A tab that has shown a signed-in page lands on Home at its next sign-in
    // instead of returning to this page (M4S-5632).
    useMarkTabInUse(session, status);

    // No-op: Spotlight is cache-only (M4S-5315). Hook kept so this layout
    // does not grow a live DANI warm-up again.
    useDiscoverInsightsAutoRefresh(session);

    useEffect(() => {
        if (pathname && !ENV.isProduction) {
            logRouteVisit(pathname);
        }
    }, [pathname]);

    // LogRocket identification lives in <LogRocketIdentify />, rendered below —
    // it has to sit inside SubsidiaryFilterProvider to see the view-as target,
    // and this layout is what renders that provider (M4S-5593).

    if (status === 'loading') {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
        );
    }

    // Freemium users get a minimal layout: no sidebar, just a header with sign-out
    if (isFreemium) {
        return (
            <main className="flex h-screen flex-col">
                <LogRocketIdentify />
                <div className="flex items-center justify-between border-b border-divider px-4 py-3">
                    <div className="flex items-center gap-2">
                        <Image
                            src="/assets/images/MFourLogo.svg"
                            as={NextImage}
                            radius="none"
                            alt="logo"
                            width={32}
                            height={32}
                        />
                        <span className="text-xl font-bold uppercase opacity-40">Studio</span>
                    </div>
                    <Button
                        className="text-default-500 data-[hover=true]:text-foreground"
                        startContent={
                            <Icon
                                className="rotate-180 text-default-500"
                                icon="solar:minus-circle-line-duotone"
                                width={20}
                                height={20}
                            />
                        }
                        variant="light"
                        onPress={() => signOut({ callbackUrl: '/sign-in' })}
                    >
                        Sign Out
                    </Button>
                </div>
                {ENV.isDevelopment && <SessionDebug />}
                <div className="flex min-h-0 flex-1">
                    <div className="flex-1 overflow-auto">
                        <RoutePermissionGuard redirectToHome={true}>
                            {children}
                        </RoutePermissionGuard>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <FloatingInputBarProvider>
            <SubsidiaryFilterProvider>
                <LogRocketIdentify />
                <div className="bg-studio-shell flex h-screen flex-col">
                    <TrialNotificationBanner />
                    <main className="flex min-h-0 flex-1 bg-transparent">
                        <WithSidebar />
                        <div className="flex min-w-0 flex-1 flex-col bg-transparent">
                            <ViewAsBanner hideOnPaths={['/builder']} />
                            <OrgSelectionDrawer />
                            <div className="flex items-center gap-x-3 p-4 sm:hidden">
                                <Button
                                    isIconOnly
                                    size="sm"
                                    variant="flat"
                                    onPress={() => {
                                        onOpenChange();
                                    }}
                                >
                                    <Icon
                                        className="text-default-500"
                                        icon="lucide:menu"
                                        width={20}
                                        height={20}
                                    />
                                </Button>
                            </div>
                            {ENV.isDevelopment && <SessionDebug />}
                            <div className="flex-1 overflow-auto bg-transparent">
                                <RoutePermissionGuard redirectToHome={true}>
                                    {children}
                                </RoutePermissionGuard>
                            </div>
                        </div>
                        {isAssistantEnabled && <PersistentAssistant />}
                        {isAssistantEnabled && <PlannerReportToasts />}
                    </main>
                </div>
            </SubsidiaryFilterProvider>
        </FloatingInputBarProvider>
    );
};

PrivateLayout.displayName = 'PrivateLayout';

export default PrivateLayout;
